import { lazy, Suspense } from 'react';
import Spinner from '@components/ui/Spinner/Spinner';
import AuthActionHandler from '@features/auth/components/AuthActionHandler';
import { AuthProvider } from '@features/auth/context/AuthContext';
import {
  ROUTE_TYPES,
  withAuthProtection,
} from '@features/auth/hoc/withAuthProtection';
import { Toaster } from 'sileo';
import { useTheme } from '@context/ThemeContext';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';

// Lazy-loaded pages
const Dashboard = lazy(() => import('@pages/Dashboard'));
const Login = lazy(() => import('@pages/auth/Login'));
const Register = lazy(() => import('@pages/auth/Register'));
const ClientList = lazy(() => import('@pages/ClientList'));
const SearchDetail = lazy(() => import('@pages/SearchDetail'));
const SalesDetail = lazy(() => import('@pages/SalesDetail'));
const Brands = lazy(() => import('@pages/Brands'));
const Marketing = lazy(() => import('@pages/Marketing'));
const SearchWeb = lazy(() => import('@pages/SearchWeb'));
const SalesWeb = lazy(() => import('@pages/SalesWeb'));
const SalesList = lazy(() => import('@pages/SalesList'));
const Discounts = lazy(() => import('@pages/Discounts'));
const IA = lazy(() => import('@pages/IA'));
const Processes = lazy(() => import('@pages/Processes'));
const Settings = lazy(() => import('@pages/Settings'));
const Conversations = lazy(() => import('@pages/Conversations'));
const Blacklist = lazy(() => import('@pages/Blacklist'));
const BrandsMongo = lazy(() => import('@pages/BrandsMongo'));

// Aplicar proteccion a las rutas
const ProtectedDashboard = withAuthProtection(Dashboard, ROUTE_TYPES.PRIVATE);
const PublicLogin = withAuthProtection(Login, ROUTE_TYPES.PUBLIC);
const PublicRegister = withAuthProtection(Register, ROUTE_TYPES.PUBLIC);
const ProtectedClientList = withAuthProtection(ClientList, ROUTE_TYPES.PRIVATE);
const ProtectedSearchDetail = withAuthProtection(
  SearchDetail,
  ROUTE_TYPES.PRIVATE
);
const ProtectedSalesDetail = withAuthProtection(
  SalesDetail,
  ROUTE_TYPES.PRIVATE
);
const ProtectedBrands = withAuthProtection(Brands, ROUTE_TYPES.PRIVATE);
const ProtectedMarketing = withAuthProtection(Marketing, ROUTE_TYPES.PRIVATE);
const ProtectedSearchWeb = withAuthProtection(SearchWeb, ROUTE_TYPES.PRIVATE);
const ProtectedSalesWeb = withAuthProtection(SalesWeb, ROUTE_TYPES.PRIVATE);
const ProtectedSalesList = withAuthProtection(SalesList, ROUTE_TYPES.PRIVATE);
const ProtectedDiscounts = withAuthProtection(Discounts, ROUTE_TYPES.PRIVATE);
const ProtectedIA = withAuthProtection(IA, ROUTE_TYPES.PRIVATE);
const ProtectedProcesses = withAuthProtection(Processes, ROUTE_TYPES.PRIVATE);
const ProtectedSettings = withAuthProtection(Settings, ROUTE_TYPES.PRIVATE);
const ProtectedConversations = withAuthProtection(
  Conversations,
  ROUTE_TYPES.PRIVATE
);
const ProtectedBlacklist = withAuthProtection(Blacklist, ROUTE_TYPES.PRIVATE);
const ProtectedBrandsMongo = withAuthProtection(
  BrandsMongo,
  ROUTE_TYPES.PRIVATE
);

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function App() {
  const { mode } = useTheme();

  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/login"
              element={<PublicLogin title="Iniciar sesion" />}
            />
            <Route
              path="/register"
              element={<PublicRegister title="Registro" />}
            />
            <Route path="/" element={<ProtectedDashboard />} />
            <Route path="/clientes" element={<ProtectedClientList />} />
            <Route
              path="/busquedas/:searchId"
              element={<ProtectedSearchDetail />}
            />
            <Route
              path="/ventas/:applicationNumber"
              element={<ProtectedSalesDetail />}
            />
            <Route path="/marcas" element={<ProtectedBrands />} />
            <Route path="/marketing" element={<ProtectedMarketing />} />
            <Route path="/busquedas-web" element={<ProtectedSearchWeb />} />
            <Route path="/ventas-web" element={<ProtectedSalesWeb />} />
            <Route path="/ventas" element={<ProtectedSalesList />} />
            <Route path="/descuentos" element={<ProtectedDiscounts />} />
            <Route path="/ia" element={<ProtectedIA />} />
            <Route path="/procesos" element={<ProtectedProcesses />} />
            <Route path="/configuracion" element={<ProtectedSettings />} />
            <Route
              path="/conversaciones"
              element={<ProtectedConversations />}
            />
            <Route path="/blacklist" element={<ProtectedBlacklist />} />
            <Route path="/marcas-mongo" element={<ProtectedBrandsMongo />} />
            <Route path="/auth/action" element={<AuthActionHandler />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="bottom-right" theme={mode} />
      </Router>
    </AuthProvider>
  );
}

export default App;
